import { useMemo, useState, useEffect } from 'react';
import {
  Download,
  Expand,
  Network,
  ChevronDown,
  ChevronUp,
  Maximize2,
  Minimize2,
} from 'lucide-react';
import { useOrgChart, useCountries, useDepartments } from '@/lib/queries';
import { initials } from '@isoft-people/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageTransition, FadeIn } from '@/components/ui/motion';

interface Node {
  id: string;
  firstName: string;
  lastName: string;
  photoUrl?: string;
  managerId?: string | null;
  position?: { title: string };
  country?: { code: string; nameEs: string };
  department?: { name: string };
  children?: Node[];
}

function exportOrgCsv(items: any[]) {
  const header = ['Colaborador', 'Puesto', 'Departamento', 'Pais', 'Jefe'];
  const byId: Record<string, any> = {};
  items.forEach((i) => (byId[i.id] = i));
  const rows = items.map((i) => {
    const jefe = i.managerId && byId[i.managerId]
      ? `${byId[i.managerId].firstName} ${byId[i.managerId].lastName}`
      : '';
    return [
      `${i.firstName} ${i.lastName}`,
      i.position?.title ?? '',
      i.department?.name ?? '',
      i.country?.nameEs ?? '',
      jefe,
    ];
  });
  const csv = [header, ...rows]
    .map((row) =>
      row
        .map((c) => {
          const s = String(c ?? '');
          return /[,"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(','),
    )
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `organigrama_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function buildTree(items: Node[]): Node[] {
  const map: Record<string, Node> = {};
  items.forEach((i) => (map[i.id] = { ...i, children: [] }));
  const roots: Node[] = [];
  items.forEach((i) => {
    if (i.managerId && map[i.managerId]) map[i.managerId].children!.push(map[i.id]);
    else roots.push(map[i.id]);
  });
  return roots;
}

const AVATAR_COLORS = [
  'from-primary to-primary-light',
  'from-accent to-green-400',
  'from-warning to-yellow-400',
  'from-error to-pink-400',
  'from-info to-cyan-400',
  'from-purple-500 to-indigo-400',
];

/** Cuenta recursivamente todos los descendientes de un nodo. */
function countDescendants(node: Node): number {
  if (!node.children || node.children.length === 0) return 0;
  return node.children.reduce((sum, c) => sum + 1 + countDescendants(c), 0);
}

function NodeCard({
  node,
  level = 0,
  compact = false,
  collapsed,
  onToggle,
}: {
  node: Node;
  level?: number;
  compact?: boolean;
  collapsed: Set<string>;
  onToggle: (id: string) => void;
}) {
  const fullName = `${node.firstName} ${node.lastName}`;
  const color = AVATAR_COLORS[level % AVATAR_COLORS.length];
  const hasChildren = !!(node.children && node.children.length > 0);
  const isCollapsed = collapsed.has(node.id);
  const directCount = node.children?.length ?? 0;

  return (
    <li className="flex flex-col items-center">
      <div
        className={`lift relative flex ${
          compact ? 'w-40' : 'w-48'
        } flex-col items-center gap-1 rounded-xl border border-border bg-white p-4 text-center shadow-sm transition hover:border-primary hover:shadow-md`}
      >
        <div
          className={`flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br ${color} text-sm font-semibold text-white shadow-sm`}
        >
          {initials(fullName)}
        </div>
        <p className="mt-1 text-sm font-semibold text-font-primary">{fullName}</p>
        <p className="text-[11px] text-font-secondary">{node.position?.title}</p>
        {!compact && node.department && (
          <span className="mt-1">
            <Badge variant="info">{node.department.name}</Badge>
          </span>
        )}

        {hasChildren && (
          <button
            onClick={() => onToggle(node.id)}
            title={isCollapsed ? 'Expandir' : 'Colapsar'}
            className={`mt-2 inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition ${
              isCollapsed
                ? 'border-primary/30 bg-primary-subtle text-primary hover:bg-primary hover:text-white'
                : 'border-border bg-white text-font-secondary hover:bg-bg'
            }`}
          >
            {isCollapsed ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
            {isCollapsed ? `${countDescendants(node)} a cargo` : `${directCount} directos`}
          </button>
        )}
      </div>

      {hasChildren && !isCollapsed && (
        <>
          <div className="h-4 w-px bg-border" />
          <ul className="flex items-start gap-4 border-t border-border pt-4">
            {node.children!.map((c) => (
              <NodeCard
                key={c.id}
                node={c}
                level={level + 1}
                compact={level >= 1}
                collapsed={collapsed}
                onToggle={onToggle}
              />
            ))}
          </ul>
        </>
      )}
    </li>
  );
}

/** Recolecta ids de nodos con hijos; si minDepth se da, solo a esa profundidad o más. */
function collectCollapsible(nodes: Node[], depth = 0, minDepth = 0, acc = new Set<string>()): Set<string> {
  for (const n of nodes) {
    if (n.children && n.children.length > 0) {
      if (depth >= minDepth) acc.add(n.id);
      collectCollapsible(n.children, depth + 1, minDepth, acc);
    }
  }
  return acc;
}

export function OrgChartPage() {
  const [countryId, setCountryId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const { data, isLoading } = useOrgChart(countryId || undefined);
  const { data: countries } = useCountries();
  const { data: departments } = useDepartments();
  const tree = useMemo(() => (data ? buildTree(data.items) : []), [data]);

  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  // Por defecto colapsa desde el nivel 1: se ven las raíces y sus directos.
  useEffect(() => {
    setCollapsed(collectCollapsible(tree, 0, 1));
  }, [tree]);

  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const expandAll = () => setCollapsed(new Set());
  const collapseAll = () => setCollapsed(collectCollapsible(tree, 0, 0));

  return (
    <PageTransition className="flex flex-col gap-6">
      {/* Header con acento de marca */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-subtle">
            <Network size={22} className="text-primary" />
          </span>
          <div>
            <h1 className="text-2xl font-bold text-font-primary">Organigrama</h1>
            <p className="mt-0.5 text-sm text-font-secondary">
              Estructura jerárquica del equipo
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={countryId}
            onChange={(e) => setCountryId(e.target.value)}
            className="h-9 rounded-md border border-border bg-white px-3 text-sm text-font-primary transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          >
            <option value="">País: Todos</option>
            {countries?.items?.map((c: any) => (
              <option key={c.id} value={c.id}>
                {c.nameEs}
              </option>
            ))}
          </select>
          <select
            value={departmentId}
            onChange={(e) => setDepartmentId(e.target.value)}
            className="h-9 rounded-md border border-border bg-white px-3 text-sm text-font-primary transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          >
            <option value="">Área: Todas</option>
            {departments?.items?.map((d: any) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
          <Button variant="secondary" size="sm" onClick={expandAll}>
            <Maximize2 size={14} /> Expandir todo
          </Button>
          <Button variant="secondary" size="sm" onClick={collapseAll}>
            <Minimize2 size={14} /> Colapsar todo
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => exportOrgCsv(data?.items ?? [])}
          >
            <Download size={14} /> Exportar CSV
          </Button>
          <Button variant="ghost" size="icon" onClick={() => window.print()}>
            <Expand size={16} />
          </Button>
        </div>
      </div>

      <FadeIn delay={0.05}>
        <div className="overflow-x-auto rounded-xl border border-border bg-white py-6 shadow-sm sm:py-8 lg:py-12">
          {/* w-max + padding interno: el padding viaja con el scroll, así la
              última tarjeta no se corta al hacer swipe horizontal. */}
          <div className="mx-auto w-max px-4 sm:px-10 lg:px-20">
          {isLoading ? (
            <div className="flex flex-wrap items-start justify-center gap-10 py-6">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={`sk-${i}`}
                  className="flex w-48 flex-col items-center gap-2 rounded-xl border border-border bg-white p-4"
                >
                  <div className="h-12 w-12 rounded-full brand-shimmer" />
                  <div className="mt-1 h-3 w-28 rounded brand-shimmer" />
                  <div className="h-2.5 w-20 rounded brand-shimmer" />
                  <div className="mt-1 h-4 w-16 rounded-full brand-shimmer" />
                </div>
              ))}
            </div>
          ) : tree.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-10 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-bg">
                <Network size={22} className="text-font-tertiary" />
              </span>
              <p className="text-sm font-medium text-font-secondary">
                No hay datos para mostrar.
              </p>
              <p className="text-xs text-font-tertiary">
                Ajusta los filtros de país o área.
              </p>
            </div>
          ) : (
            <ul className="flex items-start justify-center gap-10">
              {tree.map((root) => (
                <NodeCard
                  key={root.id}
                  node={root}
                  collapsed={collapsed}
                  onToggle={toggle}
                />
              ))}
            </ul>
          )}
          </div>
        </div>
      </FadeIn>
    </PageTransition>
  );
}
